// Modal per creare un nuovo documento HSE (DVR, POS, DUVRI, DURC, ecc.)
import { useState } from 'react'
import { X, FilePlus } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Select } from '../../../components/ui/Select'
import api from '../../../lib/api'

const DOC_TYPES = [
  // — Valutazione rischi (D.Lgs 81/2008)
  { value: 'dvr',                label: 'DVR — Documento Valutazione Rischi (art. 28)' },
  { value: 'duvri',              label: 'DUVRI — Rischi Interferenti (art. 26)' },
  { value: 'pos',                label: 'POS — Piano Operativo Sicurezza' },
  { value: 'psc',                label: 'PSC — Piano Sicurezza e Coordinamento (art. 100)' },
  { value: 'pimus',              label: 'PiMUS — Piano Montaggio/Uso/Smontaggio ponteggi' },
  // — Nomine e designazioni
  { value: 'nomina_rspp',        label: 'Nomina RSPP' },
  { value: 'nomina_rls',         label: 'Nomina RLS' },
  { value: 'nomina_medico',      label: 'Nomina Medico Competente' },
  { value: 'designazione_emergenze', label: 'Designazione addetti antincendio / primo soccorso' },
  // — Idoneità sanitaria e formazione
  { value: 'idoneita_sanitaria', label: 'Idoneità sanitaria / Sorveglianza sanitaria' },
  { value: 'attestati_formazione', label: 'Attestati formazione sicurezza' },
  { value: 'attestato_preposto', label: 'Attestato Preposto' },
  // — Idoneità tecnico-professionale appalti (art. 26)
  { value: 'visura_camerale',    label: 'Visura camerale (CCIAA)' },
  { value: 'durc',               label: 'DURC — Regolarità Contributiva' },
  { value: 'durf',               label: 'DURF — Regolarità Fiscale' },
  { value: 'autocert_itp',       label: 'Autocertificazione idoneità tecnico-professionale' },
  { value: 'patente_crediti',    label: 'Patente a crediti (cantieri)' },
  { value: 'soa',                label: 'Attestazione SOA' },
  // — Assicurazioni e certificazioni
  { value: 'polizza_rc',         label: 'Polizza RC' },
  { value: 'polizza_infortuni',  label: 'Polizza Infortuni' },
  { value: 'inail_iscrizione',   label: 'Iscrizione INAIL' },
  { value: 'iso_certificazione', label: 'Certificazione ISO' },
  { value: 'verifiche_attrezzature', label: 'Verifiche periodiche attrezzature' },
  // — Altro
  { value: 'altro',              label: 'Altro documento' },
]

interface Props {
  isOpen: boolean
  onClose: () => void
}

export function NewDocumentModal({ isOpen, onClose }: Props) {
  const qc = useQueryClient()
  const [type, setType] = useState('dvr')
  const [title, setTitle] = useState('')
  const [issueDate, setIssueDate] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [error, setError] = useState('')

  const isCustom = type === 'altro'

  const mutation = useMutation({
    mutationFn: async () => {
      if (isCustom && !title.trim()) {
        throw new Error('Inserisci un nome per il documento')
      }
      const res = await api.post('/hse/documents', {
        type,
        title: title.trim() || DOC_TYPES.find((d) => d.value === type)?.label.split(' — ')[0] || type,
        issueDate: issueDate || undefined,
        expiryDate: expiryDate || undefined,
      })
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hse', 'documents'] })
      handleClose()
    },
    onError: (err: Error) => {
      setError(err.message || 'Errore durante la creazione')
    },
  })

  const handleClose = () => {
    setType('dvr')
    setTitle('')
    setIssueDate('')
    setExpiryDate('')
    setError('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-xl bg-[var(--fc-surface)] shadow-2xl border border-[var(--fc-border)] p-6 space-y-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--fc-foreground)] flex items-center gap-2">
            <FilePlus className="h-5 w-5 text-[var(--fc-primary)]" />
            Nuovo documento HSE
          </h2>
          <button onClick={handleClose} className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tipo documento */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-[var(--fc-foreground)]">Tipo documento</label>
          <Select
            options={DOC_TYPES}
            value={type}
            onValueChange={setType}
          />
        </div>

        {/* Titolo — obbligatorio se tipo "Altro" */}
        <Input
          label={isCustom ? 'Nome documento' : 'Titolo (opzionale)'}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={isCustom ? 'Es. Certificazione antincendio sede Roma' : 'Es. DVR revisione 2024'}
          required={isCustom}
        />

        {/* Date */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Data emissione"
            type="date"
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
          />
          <Input
            label="Data scadenza"
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
          />
        </div>

        {error && (
          <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <Button variant="ghost" onClick={handleClose}>Annulla</Button>
          <Button
            onClick={() => mutation.mutate()}
            isLoading={mutation.isPending}
            disabled={mutation.isPending}
          >
            Crea documento
          </Button>
        </div>
      </div>
    </div>
  )
}
